import { Button, ButtonGroup, DeleteIcon, EditIcon, Table, TableColumns, Tooltip } from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useDefaultDivisionsQuery } from '../../../types/planung-graphql-client-defs';
import { DivisionsListDataType } from '../graphql/types';
import styles from './_DivisionsTables.module.scss';

type DivisionsDefaultTableProps = {
  onAdd: () => void;
  onDelete: (uuid: string) => void;
  onEdit: (uuid: string) => void;
};

export const DivisionsDefaultTable = ({ onAdd, onDelete, onEdit }: DivisionsDefaultTableProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('DIVISIONS');

  const { sorting, saveSorting } = useColumnsSort('divisions-list');

  const [{ data }] = useDefaultDivisionsQuery({ context });

  const tableData: DivisionsListDataType[] = useMemo(() => {
    return data ? data.defaultDivisions : [];
  }, [data]);

  //biome-ignore lint/correctness/useExhaustiveDependencies(t): suppress dependency t
  const tableColumns: TableColumns<DivisionsListDataType>[] = useMemo(() => {
    return [
      {
        header: t('common.type'),
        accessorKey: 'name',
        id: 'name',
        size: 300,
      },
      {
        id: 'totalCount',
        header: t('divisions.groupCount'),
        accessorFn: (division) => division.groups.length,
        enableGlobalFilter: false,
        enableColumnFilter: false,
        size: 125,
      },
      {
        id: 'groupsConnections',
        header: t('groups.title', { count: 2 }),
        accessorKey: 'groups',
        enableGlobalFilter: false,
        enableColumnFilter: false,
        canExpand: true,
        size: 400,
        cell: ({ row }) => {
          const value = row.original.groups;
          return (
            <div className={styles['bars-wrapper']}>
              {value.map((node) => {
                return (
                  <Tooltip key={node.uuid} triggerClass={styles['bars-item']} content={node.name}>
                    <div className={styles.name} key={node.uuid}>
                      {node.shortName}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          );
        },
      },
    ];
  }, []);

  return (
    <Table<DivisionsListDataType>
      className={styles['divisions-default-table']}
      showBorderRadius
      showShadow
      sorting={sorting}
      onSortingChange={saveSorting}
      columns={tableColumns}
      data={tableData}
      showActionBar
      actionBarSettings={{
        showAddButton: true,
      }}
      isOnWhite={false}
      onAddClick={() => onAdd()}
      lastColWidth='80px'
      lastCol={({ original }) => {
        return (
          <ButtonGroup>
            <Button hierarchy='tertiary' onClick={() => onEdit(original.uuid)} icon={<EditIcon className='small' />} />
            <Button
              hierarchy='tertiary'
              onClick={() => onDelete(original.uuid)}
              icon={<DeleteIcon className='small' color='var(--color-error)' />}
            />
          </ButtonGroup>
        );
      }}
    />
  );
};
